import { cva, type VariantProps } from 'class-variance-authority';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '../../utils/cn';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-full font-medium transition-all disabled:opacity-50 disabled:pointer-events-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-on-primary hover:bg-primary-active active:scale-95',
        secondary:
          'bg-surface-strong text-ink hover:bg-surface-strong/80 active:scale-95',
        'secondary-dark':
          'bg-surface-dark-elevated text-on-dark hover:bg-surface-dark-elevated/80 active:scale-95',
        tertiary: 'bg-transparent text-primary hover:bg-primary/10 active:scale-95',
        outline:
          'bg-transparent border-2 border-primary text-primary hover:bg-primary/5 active:scale-95',
        danger:
          'bg-semantic-down text-on-primary hover:opacity-90 active:scale-95',
        ghost:
          'bg-transparent text-ink border border-hairline hover:bg-surface-soft active:scale-95',
      },
      size: {
        sm: 'px-3 py-2 text-caption-strong h-8',
        md: 'px-5 py-3 text-button h-11',
        lg: 'px-8 py-4 text-button h-14',
        pill: 'px-8 py-4 text-button h-14',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  }
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  icon?: ReactNode;
  isLoading?: boolean;
  children?: ReactNode;
}

/**
 * Loading Spinner Component
 */
function LoadingSpinner() {
  return (
    <svg
      className="animate-spin h-4 w-4"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      ></circle>
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      ></path>
    </svg>
  );
}

/**
 * Button Component
 * Coinbase-styled button with variants and sizes
 */
export const Button = ({
  variant = 'primary',
  size = 'md',
  className,
  disabled,
  isLoading = false,
  icon,
  children,
  type = 'button',
  ...rest
}: ButtonProps) => {
  return (
    <button
      type={type}
      disabled={disabled || isLoading}
      className={cn(
        buttonVariants({
          variant,
          size,
        }),
        className
      )}
      {...rest}
    >
      {isLoading ? (
        <LoadingSpinner />
      ) : icon ? (
        <>
          {icon}
          {children && <span>{children}</span>}
        </>
      ) : (
        children
      )}
    </button>
  );
};

export { buttonVariants };

export default Button;
